import { useState, useEffect, useCallback, useMemo } from "react";
import {
  fetchUsers,
  enableDisableUser as enableDisable,
  logoutUser as logout,
} from "services/userService";
import { verifyTokenExpire } from "middleware/verifyToken";
import { ToastBottomEnd } from "views/components/Toast";
import { toastErrorMessageWithParameterConfig } from "config/toastConfig";
import { useTranslation } from "react-i18next";
import axios from "axios";
import FileSaver from "file-saver";

export const useUsers = (endpoint) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [usersLogout, setUsersLogout] = useState([]);
  const [error, setError] = useState(null);
  const [entity, setEntity] = useState(null);
  const [entityOptions, setEntityOptions] = useState([]);


  const fetchEntities = async () => {
    if (!endpoint?.staffCreate || !verifyTokenExpire()) return;
    try {
      const response = await axios.get(endpoint.staffCreate);
      if (response.data) {
        if (response.data.institutions) {
          setEntityOptions([
            {id: '', name: "Toutes les institutions"},
            ...response.data.institutions
          ]);
        } else if (response.data.units) {
          setEntityOptions([
            {id: '', name: "Toutes les unités"},
            ...response.data.units
          ]);
        }
      }
    } catch (error) {
      console.error("Error fetching entity:", error);
    }
  };

  useEffect(() => {
      fetchEntities();    
  }, [endpoint]);


  const loadUsers = useCallback(async () => {
    if (!verifyTokenExpire()) return false;

    try {
      if (!searchTerm) {
        setLoading(true);
      }

      const data = await fetchUsers(endpoint, page, perPage, searchTerm, entity);

      setUsers(data.data);
      setTotal(data.total);
      return true;
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(
        error.response?.data?.message ||
          t("Erreur lors du chargement des données")
      );
      return false;
    } finally {
      setLoading(false);
    }
  }, [endpoint, entity, page, perPage, searchTerm, t]);

  const enableDisableUser = useCallback(
    async (userId) => {
      if (!verifyTokenExpire()) return false;
      try {
        await enableDisable(endpoint, userId);
        return true;
      } catch (error) {
        console.error("Error updating user status:", error);
        if (error.response?.data?.error) {
          ToastBottomEnd.fire(
            toastErrorMessageWithParameterConfig(error.response?.data.error)
          );
        } else {
          ToastBottomEnd.fire(
            toastErrorMessageWithParameterConfig(t("Echec de l'opération"))
          );
        }
        return false;
      }
    },
    [endpoint, t]
  );

  const logoutUser = useCallback(
    async (userId) => {
      if (!verifyTokenExpire()) return false;

      try {
        const data = await logout(endpoint, userId);
        if (data) {
          setUsersLogout((prev) => {
            if (prev.indexOf(userId) === -1) {
              return [...prev, userId];
            }
            return prev;
          });
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error logging out user:", error);
        throw error;
      }
    },
    [endpoint]
  );

  const filteredUsers = useMemo(() => {
    return users;
  }, [users]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, perPage, setPage]);

  useEffect(() => {
    console.log("loadUsers");
    loadUsers();
  }, [loadUsers]);

  const downloadUsersPdf = () => {
    return new Promise((resolve, reject) => {
      axios.get(endpoint.downloadPdf(entity), { responseType: "blob" })
      .then((response) => {
        const today = new Date();
        const fileName =`users_${today.getDate()}_${today.getMonth() + 1}_${today.getFullYear()}.pdf`
        FileSaver.saveAs(
          response.data,
          fileName
        );
        resolve("success");
      })
      .catch((error) => {
        console.log(error);
        ToastBottomEnd.fire(toastErrorMessageWithParameterConfig("Echec de téléchargement du PDF"));
        setError(
          error.response?.data?.message ||
            t("Erreur lors du téléchargement des données")
        );
        reject(error);
      })
    });
  }


  const downloadUsersExcel = () => {
    return new Promise((resolve, reject) => {
      axios.get(endpoint.downloadExcel(entity), { responseType: "blob" })
      .then((response) => {
        const today = new Date();
        const fileName =`users_${today.getDate()}_${today.getMonth() + 1}_${today.getFullYear()}.xlsx`
        FileSaver.saveAs(
          response.data,
          fileName
        );
        resolve("success");
      })
      .catch((error) => {
        console.log(error);
        ToastBottomEnd.fire(toastErrorMessageWithParameterConfig("Echec de téléchargement du fichier Excel"));
        setError(
          error.response?.data?.message ||
            t("Erreur lors du téléchargement des données")
        );
        reject(error);
      })
    });
  }


  return {
    users,
    entity,
    setEntity,
    setEntityOptions,
    entityOptions,
    filteredUsers,
    loading,
    searchTerm,
    setSearchTerm,
    logoutUser,
    setUsers,
    page,
    setPage,
    perPage,
    setPerPage,
    usersLogout,
    total,
    enableDisableUser,
    refresh: loadUsers,
    error,
    downloadUsersPdf,
    downloadUsersExcel
  };
};
